import {
  Button,
  InputLabel,
  Stack,
  styled,
  useMediaQuery,
  useTheme,
  IconButton,
} from "@mui/material";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { states } from "models/states";
import {
  emailValidation,
  getPasswordConfirmValidation,
  nameValidation,
  passwordValidation,
  phoneNumberValidation,
  required,
  zipCodeValidation,
} from "models/validations";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { FormMaskedTextField } from "UI/FormMaskedTextField";
import { FormSelectField } from "UI/FormSelectField";
import { FormTextField } from "UI/FormTextField";
import { SignUpLayout } from "./SignUpLayout";
import { FormCheckbox } from "UI/FormCheckbox";
import { useRef, useState } from "react";
import { Captcha } from "./Captcha";
import { showError } from "store/reducers/errorReducer";
import { SignUpRecipientDone } from "./SignUpRecipientDone";
import { hideSpinner, showSpinner } from "store/reducers/spinnerReducer";
import { DevIndicator } from "./DevIndicator";

const GrayButton = styled(Button)`
  padding: 4px 12px;
  color: #225da4;
  background-color: #ccd4dd;
  border-radius: 4px;

  &:hover {
    background-color: #d4dce6;
  }
`;

/** @type {import("types/commonTypes").NewRecipientData}*/
const defaultFormData = {
  name: "",
  street: "",
  street2: "",
  city: "",
  state: "",
  zipCode: "",
  phone: "",
  email: "",
  website: "",
  speciality: "",
  firstName: "",
  lastName: "",
  isAgreeToTerms: false,
  code: "",
  captchaId: "",
  password: "",
  password_repeat: "",
};

export const SignUpRecipient = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isLoading = useAppSelector((state) => !!state.spinner);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isSignUpDone, setIsSignUpDone] = useState(false);

  const { control, handleSubmit, watch, setError, setValue } = useForm({
    defaultValues: defaultFormData,
  });

  const password = useRef("");
  password.current = watch("password", "");

  const onSubmit = async (data) => {
    let result;
    dispatch(showSpinner());
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/create_recipient`,
        {
          method: "PUT",
          body: JSON.stringify(data),
        }
      );
      result = await response.json();
    } catch (error) {
      dispatch(showError(error));
      dispatch(hideSpinner());
      return;
    }

    if (result?.error) {
      if (result?.error === "Invalid code") {
        setError("code", {
          message: "Invalid code",
        });
        dispatch(hideSpinner());
        return;
      }
      dispatch(showError(result?.error));
      dispatch(hideSpinner());
      return;
    }

    dispatch(hideSpinner());
    setIsSignUpDone(true);
  };

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const isAgreeToTerms = watch("isAgreeToTerms");

  if (isSignUpDone) {
    return <SignUpRecipientDone />;
  }

  return (
    <SignUpLayout>
      <DevIndicator />
      <div className="flex flex-col items-center">
        <div className="text-xl sm:text-2xl font-bold mr-4">
          Sign Up as a Recipient
        </div>
        <div className="text-center">
          Please fill out the application to become a Recipient!
        </div>
        <div className="flex flex-col justify-center items-stretch gap-4">
          <div className="bg-white mt-2 p-4 sm:p-8 rounded-2xl flex flex-col justify-center items-start gap-2 sm:gap-4 max-w-3xl relative">
            <div className="flex flex-col sm:flex-row rounded-xl gap-6">
              <form onSubmit={handleSubmit(onSubmit)}>
                <Stack direction={fullScreen ? "column" : "row"} gap={4}>
                  <Stack
                    width={fullScreen ? "100%" : "calc(50% - 1rem)"}
                    gap={1}
                  >
                    <div className="text-lg text-gray-500">
                      Practice Information
                    </div>
                    <Stack alignItems={"flex-start"}>
                      <InputLabel htmlFor="name">Name</InputLabel>
                      <FormTextField
                        id="name"
                        name="name"
                        control={control}
                        rules={{ required }}
                        InputProps={{
                          autoComplete: "off",
                          disabled: isLoading,
                        }}
                      />
                    </Stack>
                    <Stack alignItems={"flex-start"}>
                      <InputLabel htmlFor="street">Address Line 1</InputLabel>
                      <FormTextField
                        id="street"
                        name="street"
                        control={control}
                        rules={{ required }}
                        InputProps={{
                          autoComplete: "off",
                          disabled: isLoading,
                        }}
                      />
                    </Stack>
                    <Stack alignItems={"flex-start"}>
                      <InputLabel htmlFor="street22">Address Line 2</InputLabel>
                      <FormTextField
                        id="stree2"
                        name="street2"
                        control={control}
                        InputProps={{
                          autoComplete: "off",
                          disabled: isLoading,
                        }}
                      />
                    </Stack>
                    <Stack direction={"row"} gap={1}>
                      <Stack alignItems={"flex-start"}>
                        <InputLabel htmlFor="city">City</InputLabel>
                        <FormTextField
                          id="city"
                          name="city"
                          control={control}
                          rules={{ required }}
                          InputProps={{
                            autoComplete: "off",
                            disabled: isLoading,
                          }}
                        />
                      </Stack>
                      <Stack alignItems={"flex-start"} maxWidth={"40%"}>
                        <InputLabel htmlFor="state">State</InputLabel>
                        <FormSelectField
                          id="state"
                          name="state"
                          control={control}
                          options={states}
                          rules={{ required }}
                          InputProps={{
                            disabled: isLoading,
                          }}
                        />
                      </Stack>
                      <Stack alignItems={"flex-start"}>
                        <InputLabel htmlFor="zipCode">Zip Code</InputLabel>
                        <FormTextField
                          id="zipCode"
                          name="zipCode"
                          control={control}
                          rules={zipCodeValidation}
                          InputProps={{
                            autoComplete: "off",
                            disabled: isLoading,
                          }}
                        />
                      </Stack>
                    </Stack>
                    <Stack alignItems={"flex-start"}>
                      <InputLabel htmlFor="phone">Phone</InputLabel>
                      <FormMaskedTextField
                        id="phone"
                        name="phone"
                        control={control}
                        rules={phoneNumberValidation}
                        mask="999-999-9999"
                        InputProps={{
                          autoComplete: "off",
                          disabled: isLoading,
                        }}
                      />
                    </Stack>
                    <Stack alignItems={"flex-start"}>
                      <InputLabel htmlFor="website">
                        Speciality (optional)
                      </InputLabel>
                      <FormTextField
                        id="speciality"
                        name="speciality"
                        control={control}
                        InputProps={{
                          autoComplete: "off",
                          disabled: isLoading,
                        }}
                      />
                    </Stack>
                    <Stack alignItems={"flex-start"}>
                      <InputLabel htmlFor="website">
                        Website (optional)
                      </InputLabel>
                      <FormTextField
                        id="website"
                        name="website"
                        control={control}
                        InputProps={{
                          autoComplete: "off",
                          disabled: isLoading,
                        }}
                      />
                    </Stack>
                  </Stack>
                  <Stack
                    width={fullScreen ? "100%" : "calc(50% - 1rem)"}
                    gap={1}
                  >
                    <div className="text-lg text-gray-500">Personal Data</div>
                    <Stack direction={fullScreen ? "column" : "row"} gap={1}>
                      <Stack alignItems={"flex-start"}>
                        <InputLabel htmlFor="firstName">First name </InputLabel>
                        <FormTextField
                          id="firstName"
                          name="firstName"
                          control={control}
                          rules={nameValidation}
                          InputProps={{
                            autoComplete: "off",
                            disabled: isLoading,
                          }}
                        />
                      </Stack>
                      <Stack alignItems={"flex-start"}>
                        <InputLabel htmlFor="lastName">Last name</InputLabel>
                        <FormTextField
                          id="lastName"
                          name="lastName"
                          control={control}
                          rules={nameValidation}
                          InputProps={{
                            autoComplete: "off",
                            disabled: isLoading,
                          }}
                        />
                      </Stack>
                    </Stack>
                    <Stack alignItems={"flex-start"}>
                      <InputLabel htmlFor="email">Email</InputLabel>
                      <FormTextField
                        id="email"
                        name="email"
                        control={control}
                        rules={emailValidation}
                        InputProps={{
                          autoComplete: "off",
                          disabled: isLoading,
                        }}
                      />
                    </Stack>
                    <Stack alignItems={"flex-start"}>
                      <InputLabel htmlFor="password">
                        Create password
                      </InputLabel>
                      <FormTextField
                        id="password"
                        name="password"
                        control={control}
                        rules={passwordValidation}
                        type={isPasswordVisible ? "text" : "password"}
                        InputProps={{
                          endAdornment: (
                            <IconButton
                              color="info"
                              onClick={() =>
                                setIsPasswordVisible(!isPasswordVisible)
                              }
                            >
                              {isPasswordVisible ? (
                                <VisibilityOffIcon />
                              ) : (
                                <VisibilityIcon />
                              )}
                            </IconButton>
                          ),
                          autoComplete: "new-password",
                          disabled: isLoading,
                        }}
                        inputProps={{
                          autocomplete: "new-password",
                          form: {
                            autocomplete: "new-password",
                          },
                        }}
                      />
                    </Stack>
                    <Stack alignItems={"flex-start"}>
                      <InputLabel htmlFor="password_repeat">
                        Repeat password
                      </InputLabel>
                      <FormTextField
                        id="password_repeat"
                        name="password_repeat"
                        control={control}
                        rules={getPasswordConfirmValidation(password.current)}
                        type={isPasswordVisible ? "text" : "password"}
                        InputProps={{
                          endAdornment: (
                            <IconButton
                              color="info"
                              onClick={() =>
                                setIsPasswordVisible(!isPasswordVisible)
                              }
                            >
                              {isPasswordVisible ? (
                                <VisibilityOffIcon />
                              ) : (
                                <VisibilityIcon />
                              )}
                            </IconButton>
                          ),
                          autoComplete: "new-password",
                          disabled: isLoading,
                        }}
                        inputProps={{
                          autocomplete: "new-password",
                          form: {
                            autocomplete: "new-password",
                          },
                        }}
                      />
                    </Stack>
                    <Captcha control={control} setValue={setValue} />
                    <Stack alignItems={"flex-start"}>
                      <FormCheckbox
                        name="isAgreeToTerms"
                        control={control}
                        label={
                          <div>
                            I agree to{" "}
                            <Link
                              to="https://referral-stream.webflow.io/terms-of-service"
                              target="_blank"
                            >
                              Terms & Conditions
                            </Link>
                          </div>
                        }
                        disabled={isLoading}
                      />
                    </Stack>
                    <Button
                      variant="contained"
                      onClick={handleSubmit(onSubmit)}
                      disabled={isLoading || !isAgreeToTerms}
                    >
                      Create Recipient
                    </Button>
                  </Stack>
                </Stack>
              </form>
            </div>
          </div>
          <div className="flex flex-row gap-2 justify-center items-center">
            <GrayButton size="small" onClick={() => navigate("/login")}>
              Log In
            </GrayButton>
            <GrayButton
              size="small"
              onClick={() => navigate("/sign_up/sender")}
            >
              Sign Up as a Sender
            </GrayButton>
          </div>
        </div>
      </div>
    </SignUpLayout>
  );
};
